import React, { useEffect, useMemo, useState } from 'react';

import Field from '@atlaskit/form/field';
import type { OptionProps, SingleValueProps, ValueType } from '@atlaskit/select/types';
import Select from '@atlaskit/select/default';
import { components } from '@atlaskit/react-select/components';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';

import { transformStargateUrl } from '../../utils/configureUtils.ts';
import type { IssueType } from '../../providers/BulkCreateContextProvider.tsx';
import { BulkConfigureSelectOption } from '../bulk-work-create-configure/BulkConfigureSelectOption.tsx';

type IssueTypeOptionType = {
	label: string;
	value: IssueType;
	avatarUrl: string;
};

const mapIssueTypeToOption = (issueType: IssueType): IssueTypeOptionType => {
	return {
		label: issueType.name,
		value: issueType,
		avatarUrl: transformStargateUrl(issueType.iconUrl),
	};
};

const IssueTypeOption = (props: OptionProps<IssueTypeOptionType>) => (
	<components.Option {...props}>
		<BulkConfigureSelectOption label={props.label} avatarUrl={props.data.avatarUrl} />
	</components.Option>
);

const IssueTypeSingleValue = (props: SingleValueProps<IssueTypeOptionType>) => (
	<components.SingleValue {...props}>
		<BulkConfigureSelectOption label={props.data.label} avatarUrl={props.data.avatarUrl} />
	</components.SingleValue>
);

interface IssueTypeSelectorProps {
	optionsIssueTypes: IssueType[];
	value: IssueType | null;
	onChange: (issueType: IssueType | null) => void;
	isLoading: boolean;
	isDisabled: boolean;
	isRequired?: boolean;
	label: string;
}

export const IssueTypeSelector: React.FC<IssueTypeSelectorProps> = (props) => {
	const {
		optionsIssueTypes = [],
		value: controlledValue,
		onChange,
		isLoading = false,
		isDisabled = false,
		label,
	} = props;

	const [localValue, setLocalValue] = useState<IssueType | null>(controlledValue ?? null);

	useEffect(() => {
		if (controlledValue !== undefined) setLocalValue(controlledValue);
	}, [controlledValue]);

	const mappedOptions = useMemo(
		() => optionsIssueTypes.map(mapIssueTypeToOption),
		[optionsIssueTypes],
	);

	const handleChange = (selectedOption: ValueType<IssueTypeOptionType>) => {
		if (!selectedOption || !selectedOption.value) {
			return;
		}

		if (selectedOption.value === localValue) {
			return;
		}

		setLocalValue(selectedOption.value);
		onChange?.(selectedOption.value);
	};

	const selectedValue = localValue ? mapIssueTypeToOption(localValue) : null;

	return (
		<Field<ValueType<IssueTypeOptionType>>
			name="defaultIssueTypes"
			label={label}
			testId="work-create-issue-type-field"
			isRequired={props.isRequired ?? false}
		>
			{({ fieldProps: { id, ...rest } }) => (
				<AnalyticsContext data={{ attributes: { fieldName: 'workCreateFormIssueTypeSelector' } }}>
					<Select<IssueTypeOptionType>
						inputId={id}
						{...rest}
						value={selectedValue}
						options={mappedOptions}
						onChange={handleChange}
						isLoading={isLoading}
						isDisabled={isLoading || isDisabled || !selectedValue}
						components={{
							Option: IssueTypeOption,
							SingleValue: IssueTypeSingleValue,
						}}
					/>
				</AnalyticsContext>
			)}
		</Field>
	);
};
